import { create } from "zustand";
import { superChatLogApi } from "../apis/superChatLog";
import {
  ChatSessionDto,
  ChatMessageDto,
} from "../apis/types/SuperChatLogTypes";

interface ChatLogFilters {
  search?: string;
  userId?: string;
  isActive?: boolean;
  startDate?: string;
  endDate?: string;
  sessionType?: string;
  senderType?: "user" | "ai";
  status?: string;
  platform?: string;
  feedbackFilter?: "has_feedback" | "no_feedback";
  dateRange?: {
    start?: string;
    end?: string;
  };
}

interface PaginationState {
  page: number;
  pageSize: number;
  limit: number;
  total: number;
  size: number;
}

interface SuperChatLogState {
  // 세션 데이터
  sessions: ChatSessionDto[];
  currentSession: ChatSessionDto | null;

  // 메시지 데이터
  messages: ChatMessageDto[];

  // 통계 데이터
  statistics: {
    overview: {
      totalSessions: number;
      totalMessages: number;
      activeSessions: number;
      avgMessagesPerSession: number;
      avgResponseTime: number;
    };
    sessionStatus: Array<{ status: string; count: number }>;
    categories: Array<{ category: string; count: number }>;
    dailyStats: Array<{ date: string; sessions: number; messages: number }>;
    messageTypes: Array<{ type: string; count: number }>;
  } | null;

  // UI 상태
  loading: boolean;
  error: string | null;

  // 필터링 및 페이지네이션
  filters: ChatLogFilters;
  pagination: PaginationState;
}

interface SuperChatLogActions {
  // 세션 관리
  fetchSessions: (params?: {
    page?: number;
    limit?: number;
    sessionType?: string;
    sessionUserType?: string;
    status?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => Promise<void>;
  fetchSessionDetail: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  deleteSessions: (sessionIds: string[]) => Promise<void>;

  // 메시지 관리
  fetchMessages: (params?: {
    page?: number;
    limit?: number;
    sessionId?: string;
    senderType?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
  }) => Promise<void>;
  fetchSessionMessages: (
    sessionId: string,
    params?: {
      page?: number;
      limit?: number;
    }
  ) => Promise<void>;
  deleteMessage: (messageId: number) => Promise<void>;
  searchMessages: (searchParams: {
    search: string;
    sessionId?: string;
    senderType?: string;
    startDate?: string;
    endDate?: string;
  }) => Promise<void>;

  // 통계
  fetchStatistics: (params?: {
    startDate?: string;
    endDate?: string;
    sessionType?: string;
    userType?: string;
  }) => Promise<void>;

  // 내보내기
  exportToExcel: (params?: {
    startDate?: string;
    endDate?: string;
    sessionType?: string;
    userType?: string;
    format?: "sessions" | "messages";
  }) => Promise<Blob>;
  exportToCsv: (params?: {
    startDate?: string;
    endDate?: string;
    sessionType?: string;
    userType?: string;
    format?: "sessions" | "messages";
  }) => Promise<Blob>;

  // 필터링 및 페이지네이션
  setFilters: (filters: Partial<ChatLogFilters>) => void;
  setPagination: (pagination: Partial<PaginationState>) => void;

  // 상태 관리
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState: SuperChatLogState = {
  sessions: [],
  currentSession: null,
  messages: [],
  statistics: null,
  loading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    pageSize: 10,
    limit: 10,
    total: 0,
    size: 10,
  },
};

export const useSuperChatLogStore = create<
  SuperChatLogState & SuperChatLogActions
>((set, get) => ({
  ...initialState,

  // 세션 목록 조회
  fetchSessions: async (params) => {
    set({ loading: true, error: null });
    try {
      const { filters, pagination } = get();
      const currentPagination = pagination || {
        page: 1,
        pageSize: 10,
        limit: 10,
        total: 0,
        size: 10,
      };

      // 빈 값인 파라미터는 제외하여 API 요청
      const queryParams: {
        page: number;
        limit: number;
        sessionType?: string;
        sessionUserType?: string;
        status?: string;
        search?: string;
        startDate?: string;
        endDate?: string;
      } = {
        page: currentPagination.page,
        limit: currentPagination.limit,
        ...params,
      };

      // 빈 값이 아닌 경우에만 필터 파라미터 추가 (params에 이미 있는 값은 덮어쓰지 않음)
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          // params에 이미 해당 키가 있으면 필터에서 덮어쓰지 않음
          if (Object.prototype.hasOwnProperty.call(queryParams, key)) {
            return;
          }

          if (value && typeof value === "string" && value.trim() !== "") {
            queryParams[key] = value;
          } else if (value && typeof value !== "string") {
            queryParams[key] = value;
          }
        });
      }

      const result = await superChatLogApi.getSessions(queryParams);

      // API 응답 구조에 맞게 데이터 추출
      const data = result;

      // 안전한 pagination 업데이트
      const updatedPagination = {
        ...currentPagination,
        total: data?.count || 0,
        page: data?.page || currentPagination.page,
        // API 응답의 limit을 우선으로 사용하고, size도 동일하게 설정
        limit: data?.limit || currentPagination.limit || 10,
        size: data?.limit || currentPagination.limit || 10,
        pageSize: data?.limit || currentPagination.limit || 10,
      };

      set({
        sessions: data?.list || [],
        pagination: updatedPagination,
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "세션 목록을 불러오는데 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  // 세션 상세 조회
  fetchSessionDetail: async (sessionId) => {
    set({ loading: true, error: null });
    try {
      const session = await superChatLogApi.getSessionDetail(sessionId);
      set({ currentSession: session, loading: false });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "세션 상세 정보를 불러오는데 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  // 세션 삭제
  deleteSession: async (sessionId) => {
    set({ loading: true, error: null });
    try {
      await superChatLogApi.deleteSession(sessionId);

      // 목록에서 해당 세션 제거
      const { sessions } = get();
      const filteredSessions = sessions.filter(
        (session) => session.sessionUuid !== sessionId
      );

      set({
        sessions: filteredSessions,
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "세션 삭제에 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
      throw error;
    }
  },

  // 여러 세션 삭제
  deleteSessions: async (sessionIds) => {
    set({ loading: true, error: null });
    try {
      await superChatLogApi.deleteSessions(sessionIds);

      // 목록 새로고침
      await get().fetchSessions();

      set({ loading: false });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "세션 삭제에 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
      throw error;
    }
  },

  // 메시지 목록 조회
  fetchMessages: async (params) => {
    set({ loading: true, error: null });
    try {
      const { filters, pagination } = get();
      const queryParams = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
        ...params,
      };

      const result = await superChatLogApi.getMessages(queryParams);

      // API 응답을 ChatMessageDto 형식으로 변환
      const transformedMessages: ChatMessageDto[] = result.list.map(
        (msg: {
          messageId: number;
          sessionId: number;
          senderType: string;
          messageContent: string;
          sentAt: Date;
        }) => ({
          messageId: msg.messageId.toString(),
          sessionId: msg.sessionId.toString(),
          messageType: msg.senderType === "user" ? "USER" : "BOT",
          messageContent: msg.messageContent,
          regDttm: msg.sentAt
            ? new Date(msg.sentAt).toISOString()
            : new Date().toISOString(),
        })
      );

      set({
        messages: transformedMessages,
        pagination: { ...pagination, total: result.count },
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "메시지 목록을 불러오는데 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  // 세션별 메시지 조회
  fetchSessionMessages: async (sessionId, params) => {
    set({ loading: true, error: null });
    try {
      const sessionData = await superChatLogApi.getSessionMessages(
        sessionId,
        params
      );

      // 백엔드 응답에서 messages 배열과 세션 정보 추출
      const messages = sessionData.messages || [];
      const session = { ...sessionData };
      delete session.messages; // messages는 별도로 관리

      set({
        messages: messages,
        currentSession: session,
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "메시지를 불러오는데 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  // 메시지 삭제
  deleteMessage: async (messageId) => {
    set({ loading: true, error: null });
    try {
      await superChatLogApi.deleteMessage(messageId);

      // 목록에서 해당 메시지 제거
      const { messages } = get();
      const filteredMessages = messages.filter(
        (message) => message.messageId !== messageId.toString()
      );

      set({
        messages: filteredMessages,
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "메시지 삭제에 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
      throw error;
    }
  },

  // 메시지 검색
  searchMessages: async (searchParams) => {
    set({ loading: true, error: null });
    try {
      const result = await superChatLogApi.searchMessages(searchParams);

      // API 응답을 ChatMessageDto 형식으로 변환
      const transformedMessages: ChatMessageDto[] = result.list.map(
        (msg: {
          messageId: number;
          sessionId: number;
          senderType: string;
          messageContent: string;
          sentAt: Date;
        }) => ({
          messageId: msg.messageId.toString(),
          sessionId: msg.sessionId.toString(),
          messageType: msg.senderType === "user" ? "USER" : "BOT",
          messageContent: msg.messageContent,
          regDttm: msg.sentAt
            ? new Date(msg.sentAt).toISOString()
            : new Date().toISOString(),
        })
      );

      set({
        messages: transformedMessages,
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "메시지 검색에 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  // 통계 조회
  fetchStatistics: async (params) => {
    set({ loading: true, error: null });
    try {
      const statistics = await superChatLogApi.getStatistics(params);

      set({
        statistics,
        loading: false,
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "통계 데이터를 불러오는데 실패했습니다.";
      set({
        error: errorMessage,
        loading: false,
      });
    }
  },

  // Excel 내보내기
  exportToExcel: async (params) => {
    try {
      return await superChatLogApi.exportToExcel(params);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Excel 내보내기에 실패했습니다.";
      set({ error: errorMessage });
      throw error;
    }
  },

  // CSV 내보내기
  exportToCsv: async (params) => {
    try {
      return await superChatLogApi.exportToCsv(params);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "CSV 내보내기에 실패했습니다.";
      set({ error: errorMessage });
      throw error;
    }
  },

  // 필터 설정
  setFilters: (newFilters) => {
    const { filters } = get();
    set({
      filters: { ...filters, ...newFilters },
      pagination: { ...get().pagination, page: 1 },
    });
  },

  // 페이지네이션 설정
  setPagination: (newPagination) => {
    const { pagination } = get();
    const currentPagination = pagination || {
      page: 1,
      pageSize: 10,
      limit: 10,
      total: 0,
      size: 10,
    };
    const updatedPagination = {
      ...currentPagination,
      ...newPagination,
      // limit이 변경되면 size와 pageSize도 동일하게 설정
      size:
        newPagination.limit ||
        newPagination.pageSize ||
        newPagination.size ||
        currentPagination.limit ||
        currentPagination.size ||
        10,
      pageSize:
        newPagination.limit ||
        newPagination.pageSize ||
        newPagination.size ||
        currentPagination.limit ||
        currentPagination.size ||
        10,
    };
    // pagination이 null이 되지 않도록 보장
    if (updatedPagination) {
      set({ pagination: updatedPagination });
    }
  },

  // 로딩 상태 설정
  setLoading: (loading) => {
    set({ loading });
  },

  // 에러 설정
  setError: (error) => {
    set({ error });
  },

  // 에러 초기화
  clearError: () => {
    set({ error: null });
  },

  // 상태 초기화
  reset: () => {
    set(initialState);
  },
}));
